<template>
    <div class="simple-toast" :class="type">
        <i :class="icon" class="i-andy-m simple-toast__icon" aria-hidden="true"></i>

        <div class="simple-toast__content" :class="{ 'is-expanded': isExpanded }">
            <p v-if="parse && parsedText && !isCollapsed" ref="textEl" class="simple-toast__text" :class="{ 'is-collapsed': isCollapsed }" v-html="parsedText"></p>
            <p v-else-if="text" ref="textEl" class="simple-toast__text" :class="{ 'is-collapsed': isCollapsed }">
                {{ text }}
            </p>

            <button v-if="showToggle" type="button" class="simple-toast__toggle" @click="toggle">
                {{ isExpanded ? $t('label.less') : $t('label.more') }}
            </button>
        </div>
    </div>
</template>

<script>
export default {
    name: 'SimpleToast',
    props: {
        icon: {
            type: String,
            default: 'i-andy-file-attach'
        },
        text: {
            type: String,
            required: true
        },
        includeToggleButton: {
            type: Boolean,
            default: true
        },
        type: {
            type: String,
            default: 'checklist-description'
        },
        parse: {
            type: Boolean,
            default: false
        }
    },
    data() {
        return {
            isExpanded: false,
            isOverflowing: false
        }
    },
    computed: {
        showToggle() {
            return this.includeToggleButton && this.isOverflowing
        },
        isCollapsed() {
            if (this.isExpanded) {
                return false
            }

            if (!this.includeToggleButton) {
                return false
            }

            return this.isOverflowing
        },
        parsedText() {
            if (typeof this.text !== 'string') {
                return this.text
            }

            if (typeof escapeHtml !== 'function' || typeof msgParse !== 'function') {
                return null
            }

            return msgParse(escapeHtml(this.text))
        }
    },
    mounted() {
        this.scheduleOverflowCheck()
        window.addEventListener('resize', this.scheduleOverflowCheck)
    },
    beforeDestroy() {
        window.removeEventListener('resize', this.scheduleOverflowCheck)
        if (this.checkRaf) cancelAnimationFrame(this.checkRaf)
    },
    methods: {
        toggle() {
            this.isExpanded = !this.isExpanded
        },
        scheduleOverflowCheck() {
            if (this.checkRaf) cancelAnimationFrame(this.checkRaf)
            this.checkRaf = requestAnimationFrame(() => {
                this.checkOverflow()
            })
        },
        checkOverflow() {
            const el = this.$refs.textEl
            if (!el) {
                this.isOverflowing = false
                return
            }

            const textRange = document.createRange()
            textRange.selectNodeContents(el)
            const hasMultipleVisualLines = textRange.getClientRects().length > 1

            // Simulamos el texto en una sola línea para medir overflow horizontal.
            // En <br> usamos `display: none` temporalmente para excluirlos del layout durante la medición.
            const previousWhiteSpace = el.style.whiteSpace
            const previousOverflow = el.style.overflow
            const brElements = Array.from(el.querySelectorAll('br'))
            const previousBrDisplays = brElements.map((br) => br.style.display)

            el.style.whiteSpace = 'nowrap'
            el.style.overflow = 'hidden'
            brElements.forEach((br) => {
                br.style.display = 'none'
            })

            const hasHorizontalOverflow = el.scrollWidth > el.clientWidth + 1
            this.isOverflowing = hasMultipleVisualLines || hasHorizontalOverflow

            el.style.whiteSpace = previousWhiteSpace
            el.style.overflow = previousOverflow
            brElements.forEach((br, index) => {
                br.style.display = previousBrDisplays[index]
            })
        }
    }
}
</script>

<style lang="scss" scoped>
.simple-toast {
    display: flex;
    align-items: center;
    column-gap: 8px;
    width: 100%;
    padding: 8px;
    border-radius: 6px;
    box-sizing: border-box;

    p {
        padding: 0 !important;
    }

    &.checklist-description {
        background-color: $color-neutral-300;

        .simple-toast__text,
        .simple-toast__icon {
            color: $neutro-s80;
        }
    }

    &__content {
        display: flex;
        align-items: center;
        column-gap: 8px;
        row-gap: 8px;
        width: 100%;
        max-width: 100%;
        max-height: 30vh;
        overflow-y: auto;

        &.is-expanded {
            flex-direction: column;
            align-items: flex-start;

            .simple-toast__toggle {
                align-self: flex-end;
            }
        }
    }

    &__text {
        flex: 1 1 auto;
        min-width: 0;
        width: 100%;
        max-width: 100%;
        font-size: 14px;
        height: 100%;
        max-height: 100%;

        // ::v-deep es necesario porque el contenido insertado con v-html no recibe
        // el atributo data-v-* que Vue aplica a los estilos scoped.
        ::v-deep a {
            color: $color-secondary-500;
            text-decoration: underline;
            word-break: break-all;
        }

        &.is-collapsed {
            white-space: nowrap;
            overflow: hidden;
            text-overflow: ellipsis;
        }
    }

    &__toggle {
        flex-shrink: 0;
        width: fit-content;
        background: none;
        border: 0;
        cursor: pointer;
        font-family: $text-bold;
        color: $color-secondary-500;
        font-size: 14px;
        line-height: 1.5;
        text-decoration: underline;
        white-space: nowrap;
        padding: 0 8px;

        &:hover,
        &:focus {
            outline: none;
        }
    }

    &__icon {
        flex-shrink: 0;
        color: $main-text;
        line-height: 1.5;
    }
}
</style>
